import React, { useRef, useState } from 'react';
import { VStack, BoxProps, Text, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styled from 'styled-components';
import { RefreshIcon } from '../../../../../components/icons';
import { Content } from './shared/types';
import { ContentComponent } from './shared/ContentComponent';
import { useIsMobile } from '../../../../../hooks/isMobile';

type Card = {
  content?: Content[];
  alternativeContent?: Content[];
};

interface CardFlipProps extends BoxProps {
  cards?: Card[];
  card?: Card;
  debug?: boolean;
}

// const rotate = keyframes`
//   0% {
//     transform: rotateY(0deg);
//   }

//   50% {
//     transform: rotateY(90deg);
//   }

//   100% {
//     transform: rotateY(180deg);
//   }
//  `;

const StyledVStack = styled(VStack)`
  transition: all 350ms ease-in-out;
  transform: rotateY(0deg);
  &.alternative {
    transform: rotateY(90deg);
  }
` as typeof VStack;

export const mapAlign = (align: Content['align']) => {
  switch (align) {
    case 'left':
      return 'start';
    case 'center':
      return 'center';
    case 'right':
      return 'end';
    case 'justify':
      return 'stretch';
  }
  return align;
};

const CardFlip: React.FC<CardFlipProps> = ({
  cards,
  card,
  debug,
  ...props
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const [state, setState] = useState([]);
  const [isAlternative, setIsAlternative] = useState(false);
  const [isAlternativeContent, setIsAlternativeContent] = useState(false);
  const [minWidth, setMinWidth] = useState<number | undefined>(undefined);
  const isMobile = useIsMobile();
  const propsToPass = minWidth ? { ...props, minWidth } : props;

  const content = (
    <>
      {card?.content?.map((c, index) => (
        <React.Fragment key={`content-${index}`}>
          {ContentComponent({ width: 'full', ...c })}
        </React.Fragment>
      ))}
    </>
  );

  const alternativeContent = (
    <>
      {card?.alternativeContent?.map((c, index) => (
        <React.Fragment key={`alt-content-${index}`}>
          {ContentComponent({ ...c, w: 'full' })}
        </React.Fragment>
      ))}
    </>
  );

  const ret = (
    <StyledVStack
      ref={ref}
      borderRadius="8px"
      py={4}
      px={4}
      boxShadow="0 3px 15px rgba(0, 0, 0, 0.1)"
      className={classNames({ alternative: isAlternative })}
      spacing={0}
      justify="space-between"
      gap={4}
      {...propsToPass}
      height="100%"
      width="100%"
      maxWidth={isMobile ? '100%' : '450px'}
      maxHeight={isMobile ? '450px' : '550px'}
    >
      <VStack w="full" h="full" spacing={0} gap={2} maxH="500px">
        {isAlternativeContent ? alternativeContent : content}
      </VStack>
      <Button
        size="sm"
        borderRadius="16px"
        leftIcon={<RefreshIcon boxSize="16px" />}
        aria-label="Rotate"
        colorScheme="blue"
        minHeight="30px"
        onClick={() => {
          if (ref.current) {
            setMinWidth(ref.current.clientWidth);
          }
          setIsAlternative(true);
          setTimeout(() => {
            setIsAlternativeContent(!isAlternativeContent);
            setIsAlternative(false);
          }, 350);
          setTimeout(() => {
            setMinWidth(undefined);
          }, 700);
        }}
      >
        {t('plugin.cardFlip.turn')}
      </Button>
    </StyledVStack>
  );

  if (debug) {
    return (
      <VStack>
        {ret}
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return <>{ret}</>;
};

export default CardFlip;
